import { forkJoin, of } from 'rxjs';
// Angular
import { Injectable } from '@angular/core';
// RxJS
import { map, mergeMap, catchError } from 'rxjs/operators';
// NGRX
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// CRUD
import { QueryParamsModel } from '../../_base/crud';
// Services
import { PatientWeightsService } from '../_services/patient-weights.service';
// State
import { AppState } from '../../reducers';
// Actions
import {
  PatientWeightActionToggleLoading,
  PatientWeightActionTypes,
  PatientWeightCreated,
  PatientWeightOnServerCreated,
  PatientWeightsPageLoaded,
  PatientWeightsPageRequested,
  PatientWeightsPageToggleLoading,
  PatientWeightStatusUpdated,
  PatientWeightsStatusUpdated,
  PatientWeightUpdated,
  ManyPatientWeightsDeleted,
  OnePatientWeightDeleted,
  ThrowPatientWeightError,
  ClearPatientWeightErrors,
  PatientWeightsStatusOnServerUpdated,
  PatientWeightStatusOnServerUpdated,
  PatientWeightOnServerUpdated,
  ManyPatientWeightsOnServerDeleted,
  OnePatientWeightOnServerDeleted,
  PatientWeightsPageCancelled,
} from '../_actions/patient-weight.actions';
import { Router } from '@angular/router';

@Injectable()
export class PatientWeightEffects {
  showPageLoadingDistpatcher = new PatientWeightsPageToggleLoading({ isLoading: true });
  showActionLoadingDistpatcher = new PatientWeightActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PatientWeightActionToggleLoading({ isLoading: false });

  @Effect()
  loadPatientWeightsPage$ = this.actions$.pipe(
    ofType<PatientWeightsPageRequested>(PatientWeightActionTypes.PatientWeightsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.patientsService.findPatientWeights(payload.page, payload.id);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PatientWeightsPageCancelled();
            } else {
              this.store.dispatch(new ThrowPatientWeightError({ error: response[0].error }));
              return new PatientWeightsPageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearPatientWeightErrors());
            const sortedResult = result.data.sort(function (a, b) {
              const x = a['start_date'];
              const y = b['start_date'];
              return x < y ? -1 : x > y ? 1 : 0;
            });
            const mappedResult = [...Array(sortedResult.length).keys()].map((i) =>
              Object.assign(result.data[i], { id: i + 1 }),
            );
            return new PatientWeightsPageLoaded({
              patientWeights: mappedResult,
              totalCount: result.count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePatientWeight$ = this.actions$.pipe(
    ofType<OnePatientWeightOnServerDeleted>(
      PatientWeightActionTypes.OnePatientWeightOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.deletePatientWeight(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientWeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientWeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new OnePatientWeightDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  deletePatientWeights$ = this.actions$.pipe(
    ofType<ManyPatientWeightsOnServerDeleted>(
      PatientWeightActionTypes.ManyPatientWeightsOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.deletePatientWeights(payload.ids).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowPatientWeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientWeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new ManyPatientWeightsDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientWeight$ = this.actions$.pipe(
    ofType<PatientWeightOnServerUpdated>(PatientWeightActionTypes.PatientWeightOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.updatePatientWeight(payload.patientWeight).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientWeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientWeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientWeightUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientWeightStatus$ = this.actions$.pipe(
    ofType<PatientWeightStatusOnServerUpdated>(
      PatientWeightActionTypes.PatientWeightStatusOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.updateStatusForPatientWeight(payload.id, payload.is_active).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowPatientWeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientWeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientWeightStatusUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientWeightsStatus$ = this.actions$.pipe(
    ofType<PatientWeightsStatusOnServerUpdated>(
      PatientWeightActionTypes.PatientWeightsStatusOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService
        .updateStatusForPatientWeights(payload.ids, payload.is_active)
        .pipe(
          catchError((error) => {
            return of({ error });
          }),
          map((res) => {
            if (res.error) {
              return new ThrowPatientWeightError({ error: res.error });
            } else {
              this.store.dispatch(new ClearPatientWeightErrors());
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              return new PatientWeightsStatusUpdated(payload);
            }
          }),
        );
    }),
  );

  @Effect()
  createPatientWeight$ = this.actions$.pipe(
    ofType<PatientWeightOnServerCreated>(PatientWeightActionTypes.PatientWeightOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.createPatientWeight(payload.patientWeight, payload.id).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientWeightError({ error: res });
          } else {
            this.store.dispatch(new ClearPatientWeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientWeightCreated({ patientWeight: res.data });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private patientsService: PatientWeightsService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
